import { AppInfo, GetMapOfSpeakerLayoutResponse } from 'types/services/hearSpace';

/**
 * Compute the finite min/max of a 2D SPL matrix.
 *
 * The heat-map colours these per-point dBA values directly, so the legend
 * bounds must bracket this range for the colour scale (blue → yellow → red) to
 * span the actual data instead of a fixed window. Returns null when the matrix
 * has no finite values, so callers can fall back to a safe default.
 */
const matrixExtent = (
  matrix: number[][] | null | undefined,
): { min: number; max: number } | null => {
  let min = Infinity;
  let max = -Infinity;
  if (matrix) {
    for (const row of matrix) {
      for (const value of row) {
        if (Number.isFinite(value)) {
          if (value < min) min = value;
          if (value > max) max = value;
        }
      }
    }
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
  return { min: Math.floor(min), max: Math.ceil(max) };
};

export interface PlotData {
  id: string;
  receiverSpacing: number;
  receiverXOffset: number;
  receiverYOffset: number;
  mapArray: number[][];
  averageTarget: number | undefined;
  directAwtdSplMatrix: number[][];
  totalAwtdSplMatrix: number[][] | null | undefined;
  stiWithNoiseMaskingMatrix: number[][] | null | undefined;
  directAwtdSpl: number | undefined;
  directAwtdSplStd: number | undefined;
  totalAwtdSpl: number | undefined;
  totalAwtdSplStd: number | undefined;
  stiWithNoiseMasking: number | undefined;
  stiWithNoiseMaskingStd: number | undefined;
  directAwtdSplLegendMin: number;
  directAwtdSplLegendMax: number;
  totalAwtdSplLegendMin: number;
  totalAwtdSplLegendMax: number;
  hearSpaceData: {
    standardDeviation: number | undefined;
    receiverXCount: number | undefined;
    receiverYCount: number | undefined;
    receiverSpacing: number | undefined;
    appInfo: AppInfo | undefined;
  };
}

/**
 * Convert a HearSpace API response to the plotData format used by the Redux
 * store and rendering components.
 *
 * Returns null when the response lacks the required SPL matrix (e.g. API error
 * or DirectSoundOnly request with no matrix populated).
 */
export const mapResponseToPlotData = (
  response: GetMapOfSpeakerLayoutResponse,
  areaId: string,
): PlotData | null => {
  if (!response.Performance || !response.Performance.DirectSplAwtdMatrix) {
    return null;
  }

  const { Performance } = response;
  const matrix = Performance.DirectSplAwtdMatrix;
  const totalMatrix = Performance.TotalSplAwtdMatrix || matrix;
  const receiverXOffset = Performance.ReceiverXOffset || 0;
  const receiverYOffset = Performance.ReceiverYOffset || 0;

  // Scale the legend colour range to the actual data. The new HearSpace API no
  // longer returns legend bounds, so derive them from the SPL matrices (the
  // values the heat map colours). Fall back to a mean ± 10 dB window, then to a
  // fixed range, when a matrix has no finite values. (PIG-219)
  const directExtent = matrixExtent(matrix);
  const totalExtent = matrixExtent(totalMatrix);
  const directAwtdSplLegendMin = directExtent
    ? directExtent.min
    : Performance.DirectSplAwtd ? Performance.DirectSplAwtd - 10 : 60;
  const directAwtdSplLegendMax = directExtent
    ? directExtent.max
    : Performance.DirectSplAwtd ? Performance.DirectSplAwtd + 10 : 100;
  const totalAwtdSplLegendMin = totalExtent
    ? totalExtent.min
    : Performance.TotalSplAwtd ? Performance.TotalSplAwtd - 10 : 60;
  const totalAwtdSplLegendMax = totalExtent
    ? totalExtent.max
    : Performance.TotalSplAwtd ? Performance.TotalSplAwtd + 10 : 100;

  return {
    id: `${areaId} full`,
    receiverSpacing: Performance.ReceiverSpacing || 0.5,
    receiverXOffset,
    receiverYOffset,
    mapArray: matrix,
    averageTarget: Performance.DirectSplAwtd,
    // Store matrices with lowercase keys for compatibility with devicePlots.js
    directAwtdSplMatrix: matrix,
    totalAwtdSplMatrix: Performance.TotalSplAwtdMatrix || matrix,
    stiWithNoiseMaskingMatrix: Performance.StiWithNoiseMaskingMatrix,
    // SPL averages for relative-average calculation
    directAwtdSpl: Performance.DirectSplAwtd,
    directAwtdSplStd: Performance.DirectSplAwtdStd,
    totalAwtdSpl: Performance.TotalSplAwtd || Performance.DirectSplAwtd,
    totalAwtdSplStd: Performance.TotalSplAwtdStd || Performance.DirectSplAwtdStd,
    stiWithNoiseMasking: Performance.StiWithNoiseMasking,
    stiWithNoiseMaskingStd: Performance.StiWithNoiseMaskingStd,
    // Legend bounds derived from the SPL matrices so the colour scale adapts to
    // the actual min/max of the data (see matrixExtent above). (PIG-219)
    directAwtdSplLegendMin,
    directAwtdSplLegendMax,
    totalAwtdSplLegendMin,
    totalAwtdSplLegendMax,
    hearSpaceData: {
      standardDeviation: Performance.DirectSplAwtdStd,
      receiverXCount: Performance.ReceiverXCount,
      receiverYCount: Performance.ReceiverYCount,
      receiverSpacing: Performance.ReceiverSpacing,
      appInfo: response.AppInfo,
    },
  };
};

/**
 * Apply non-linear scaling to CBC bar speaker SPL data.
 *
 * CBC bar speakers produce a narrow beam with a highly peaked SPL distribution.
 * Raw API values above the mean appear disproportionately hot on the heat map.
 * This transform compresses values above the yellow threshold (mean + 3 dB)
 * using a square-root curve, and corrects averageTarget to the unscaled mean
 * so the legend reflects the actual listening-level average.
 */
export const applyBarSpeakerScaling = (plotData: PlotData): PlotData => {
  const matrix: number[][] = plotData.mapArray;
  const allValues = matrix.flat();
  const mean = allValues.reduce((sum, v) => sum + v, 0) / allValues.length;
  const yellowLevel = mean + 3;
  const maxVal = allValues.reduce((max, v) => (v > max ? v : max), -Infinity);
  const factor = Math.sqrt((3.5 - 3) / Math.pow(maxVal - yellowLevel, 2));
  const scaledMatrix = matrix.map(line =>
    line.map(point => {
      if (point >= yellowLevel) {
        const aboveYellow = point - yellowLevel;
        return mean + Math.pow(aboveYellow * factor, 2) + 3;
      }
      return point;
    })
  );
  return { ...plotData, averageTarget: mean, mapArray: scaledMatrix };
};
